import { LAYOUT_COLS, LAYOUT_SCHEMA_VERSION, compactLayout } from './dashboard-layout.js';
import { WIDGETS_BY_ID } from './dashboard-widgets.js';
/**
 * The built-in dashboards, one per kind of reader. Each leads with at most
 * four headline figures that reader acts on, then the detail behind them, then
 * a row each for MLF and MFF on their own.
 *
 * Conversion and the other traffic figures need Google Analytics; where it
 * is not connected, their tiles show a sales figure instead (average order
 * value on Executive, average selling price on Pricing).
 *
 * Each preset has a version. A personal copy remembers the version it was
 * made from, so when a preset gains widgets its owner is told rather than
 * left on the old one without knowing.
 */
export const DASHBOARD_PRESET_KEYS = ['executive', 'operations', 'pricing'];
function build(placements) {
    const counts = new Map();
    const items = placements.map((p) => {
        const def = WIDGETS_BY_ID.get(p.widget);
        if (!def)
            throw new Error(`Preset names unknown widget ${p.widget}`);
        const n = (counts.get(p.widget) ?? 0) + 1;
        counts.set(p.widget, n);
        return {
            ...p,
            i: `${p.widget}-${n}`,
            minW: def.minSize.w,
            minH: def.minSize.h,
            config: def.configSchema.parse(p.config ?? {}),
        };
    });
    return { schemaVersion: LAYOUT_SCHEMA_VERSION, cols: LAYOUT_COLS, rowHeight: 80, items: compactLayout(items) };
}
const kpiRow = (y, metrics, scope) => metrics.map((metric, index) => ({
    widget: 'kpi',
    x: index * 3,
    y,
    w: 3,
    h: 2,
    config: { metric },
    ...(scope ? { scope } : {}),
}));
/** A header and a KPI row pinned to each site, starting at row y. */
const siteRows = (y, metrics) => [
    { widget: 'section-header', x: 0, y, w: 12, h: 1, config: { text: 'MLF — Log Furniture' } },
    ...kpiRow(y + 1, metrics, 'mlf'),
    { widget: 'section-header', x: 0, y: y + 3, w: 12, h: 1, config: { text: 'MFF — Farmhouse' } },
    ...kpiRow(y + 4, metrics, 'mff'),
];
export const DASHBOARD_PRESETS = {
    executive: {
        key: 'executive',
        name: 'Executive',
        description: 'Revenue against target, where it is heading, where it comes from — and how well visits convert.',
        defaultFor: ['admin', 'viewer'],
        version: 3,
        layout: build([
            { widget: 'kpi', x: 0, y: 0, w: 3, h: 2, config: { metric: 'net_sales' } },
            { widget: 'target-pace', x: 3, y: 0, w: 3, h: 2, config: { variant: 'tile' } },
            { widget: 'kpi', x: 6, y: 0, w: 3, h: 2, config: { metric: 'orders' } },
            { widget: 'traffic-kpi', x: 9, y: 0, w: 3, h: 2, config: { metric: 'site_conversion', fallbackMetric: 'aov' } },
            { widget: 'metric-trend', x: 0, y: 2, w: 8, h: 4, config: { metric: 'net_sales', groupBy: 'site' } },
            { widget: 'insights', x: 8, y: 2, w: 4, h: 8, config: { audience: 'executive' } },
            { widget: 'target-pace', x: 0, y: 6, w: 8, h: 4 },
            { widget: 'conversion-trend', x: 0, y: 10, w: 8, h: 4 },
            { widget: 'forecast', x: 8, y: 10, w: 4, h: 4 },
            { widget: 'site-split', x: 0, y: 14, w: 4, h: 4 },
            { widget: 'channel-mix', x: 4, y: 14, w: 4, h: 4 },
            { widget: 'traffic-sources', x: 8, y: 14, w: 4, h: 4 },
            { widget: 'proceeds-waterfall', x: 0, y: 18, w: 4, h: 4 },
            { widget: 'seasonality', x: 4, y: 18, w: 4, h: 4 },
            { widget: 'forecast-accuracy', x: 8, y: 18, w: 4, h: 4 },
            { widget: 'top-products', x: 0, y: 22, w: 6, h: 5 },
            { widget: 'top-movers', x: 6, y: 22, w: 6, h: 5 },
            ...siteRows(27, ['net_sales', 'orders', 'aov', 'units']),
            { widget: 'data-coverage', x: 0, y: 33, w: 12, h: 3 },
        ]),
    },
    operations: {
        key: 'operations',
        name: 'Channel & operations',
        description: 'Data health, refunds, open orders and what needs a decision.',
        defaultFor: [],
        version: 3,
        layout: build([
            ...kpiRow(0, ['refund_rate']),
            { widget: 'open-orders', x: 3, y: 0, w: 3, h: 2, config: { variant: 'tile' } },
            { widget: 'sales-review', x: 6, y: 0, w: 3, h: 2, config: { variant: 'tile' } },
            { widget: 'data-coverage', x: 9, y: 0, w: 3, h: 2, config: { variant: 'tile' } },
            { widget: 'insights', x: 0, y: 2, w: 12, h: 4, config: { audience: 'operations' } },
            { widget: 'data-coverage', x: 0, y: 6, w: 12, h: 3 },
            { widget: 'ga-reconciliation', x: 0, y: 9, w: 6, h: 4 },
            { widget: 'funnel', x: 6, y: 9, w: 6, h: 4 },
            { widget: 'metric-trend', x: 0, y: 13, w: 8, h: 4, config: { metric: 'orders', groupBy: 'channel', chart: 'bar' } },
            { widget: 'breakdown-table', x: 8, y: 13, w: 4, h: 4, config: { dimension: 'status' } },
            { widget: 'open-orders', x: 0, y: 17, w: 6, h: 4 },
            { widget: 'metric-trend', x: 6, y: 17, w: 6, h: 4, config: { metric: 'refund_rate' } },
            { widget: 'dow-hour-heatmap', x: 0, y: 21, w: 6, h: 4 },
            { widget: 'breakdown-table', x: 6, y: 21, w: 6, h: 4, config: { dimension: 'region' } },
            ...siteRows(25, ['orders', 'refund_rate', 'shipping', 'discount_rate']),
        ]),
    },
    pricing: {
        key: 'pricing',
        name: 'Pricing & listings',
        description: 'What sells, how fast, at what price — how often it is viewed, and what price changes did.',
        defaultFor: ['editor'],
        version: 3,
        layout: build([
            { widget: 'kpi', x: 0, y: 0, w: 3, h: 2, config: { metric: 'units_per_day' } },
            { widget: 'traffic-kpi', x: 3, y: 0, w: 3, h: 2, config: { metric: 'item_view_to_purchase', fallbackMetric: 'avg_selling_price' } },
            { widget: 'dead-stock', x: 6, y: 0, w: 3, h: 2, config: { variant: 'tile' } },
            { widget: 'kpi', x: 9, y: 0, w: 3, h: 2, config: { metric: 'products_sold' } },
            { widget: 'insights', x: 0, y: 2, w: 6, h: 5, config: { audience: 'pricing' } },
            { widget: 'top-movers', x: 6, y: 2, w: 6, h: 5 },
            { widget: 'price-impact', x: 0, y: 7, w: 12, h: 4 },
            { widget: 'sku-conversion', x: 0, y: 11, w: 8, h: 5 },
            { widget: 'funnel', x: 8, y: 11, w: 4, h: 5 },
            { widget: 'top-products', x: 0, y: 16, w: 6, h: 5, config: { metric: 'units' } },
            { widget: 'dead-stock', x: 6, y: 16, w: 6, h: 5 },
            { widget: 'metric-trend', x: 0, y: 21, w: 8, h: 4, config: { metric: 'units', groupBy: 'site', chart: 'bar' } },
            { widget: 'site-split', x: 8, y: 21, w: 4, h: 4, config: { metric: 'units' } },
            ...siteRows(25, ['units_per_day', 'avg_selling_price', 'products_sold', 'net_sales']),
        ]),
    },
};
export function presetForRole(role) {
    return (Object.values(DASHBOARD_PRESETS).find((preset) => preset.defaultFor.includes(role)) ?? DASHBOARD_PRESETS.executive);
}
export const isDashboardPresetKey = (value) => typeof value === 'string' && DASHBOARD_PRESET_KEYS.includes(value);
//# sourceMappingURL=dashboard-presets.js.map